// @/Hooks/ClassroomInfoApi/useClassroomJoin.ts
import {useRef, useState} from 'react';
import {ScreenName} from '@/navigators/StackConstants';
import useGetActiveMeetingsApi from './useGetActiveMeetingsApi';
import useClassroomEndMeetingApi from './useClassroomEndMeetingApi';
import {openPopup} from '@/Utils/CommonUtils';

interface JoinData {
  webinar_id: string;
  registrant_token: string;
  meeting_password: string;
  occurence_id: string;
  meeting_role: string;
  is_zoom_meeting: number | boolean;
  batch_type: string;
  webinar_topic: string;
}

interface UseClassroomJoinReturn {
  showActiveMeetingsModal: boolean;
  activeMeetingsCount: number;
  activeLoading: boolean;
  endLoading: boolean;
  handleJoinClick: (joinData: JoinData) => boolean;
  handleModalClose: () => void;
  handleModalJoin: () => void;
  handleModalEndAndJoin: () => void;
}

export default function useClassroomJoin(): UseClassroomJoinReturn {
  const hasCheckedPopupRef = useRef(false);
  const [showActiveMeetingsModal, setShowActiveMeetingsModal] = useState(false);
  const [activeMeetingsData, setActiveMeetingsData] = useState<any>(null);
  const [pendingJoinData, setPendingJoinData] = useState<JoinData | null>(null);

  // Open classroom in new tab with zoom data
  const joinClassroom = (joinData: JoinData) => {
    localStorage.setItem(
      'ZoomUserData',
      JSON.stringify({
        token: joinData.registrant_token,
        meetingId: joinData.webinar_id,
        password: joinData.meeting_password,
        occurenceId: joinData.occurence_id,
        meetingRole: joinData.meeting_role,
        isZoomMeeting: joinData.is_zoom_meeting,
        batchType: joinData.batch_type,
        topic: joinData.webinar_topic,
      }),
    );
    // const url = `${window.location.origin}/${ScreenName.Classroom}`;
    const url = 'https://learn.circlechess.com/Classroom';
    const popup = openPopup(url);

    const isPopupOpened = !!popup;
    if (!hasCheckedPopupRef.current) {
      hasCheckedPopupRef.current = true;
    }
    if (!isPopupOpened) {
      return false;
    }

    setPendingJoinData(null);
    return true;
  };

  const handleOnSuccessGetActiveMeetings = (data: any) => {
    console.log('Active meetings response:', data);

    if (data?.success && data?.active_meetings_count > 0) {
      setActiveMeetingsData(data);
      setShowActiveMeetingsModal(true);
    } else {
      if (pendingJoinData) {
        const opened = joinClassroom(pendingJoinData);
        if (!opened) {
          return;
        }
      }
    }
  };

  const handleOnErrorGetActiveMeetings = (error: any) => {
    console.error('Error fetching active meetings:', error);
    if (pendingJoinData) {
      const opened = joinClassroom(pendingJoinData);
      if (!opened) {
        return;
      }
    }
  };

  const {mutate: getActiveMeetings, isLoading: activeLoading} =
    useGetActiveMeetingsApi(
      handleOnSuccessGetActiveMeetings,
      handleOnErrorGetActiveMeetings,
    );

  const handleOnSuccessEndMeeting = (data: any) => {
    console.log('End meeting response:', data);
    if (pendingJoinData) {
      const opened = joinClassroom(pendingJoinData);
      if (!opened) {
        return;
      }
    }
  };

  const handleOnErrorEndMeeting = (error: any) => {
    console.error('Error ending meetings:', error);
    setShowActiveMeetingsModal(false);
    if (pendingJoinData) {
      const opened = joinClassroom(pendingJoinData);
      if (!opened) {
        return;
      }
    }
  };

  const {mutate: endClassroomMeeting, isLoading: endLoading} =
    useClassroomEndMeetingApi(
      handleOnSuccessEndMeeting,
      handleOnErrorEndMeeting,
    );

  // Main join handler
  const handleJoinClick = (joinData: JoinData) => {
    const {meeting_role, is_zoom_meeting, webinar_id} = joinData;

    // Student/Panelist flow - join directly
    if (meeting_role !== 'host' || is_zoom_meeting === 0) {
      return joinClassroom(joinData);
    }
    // Host flow - check for active meetings
    setPendingJoinData(joinData);
    getActiveMeetings({meetingId: webinar_id});
    return true;
  };

  // Modal handlers
  const handleModalClose = () => {
    setShowActiveMeetingsModal(false);
    setPendingJoinData(null);
    setActiveMeetingsData(null);
  };

  const handleModalJoin = () => {
    setShowActiveMeetingsModal(false);
    if (pendingJoinData) {
      const opened = joinClassroom(pendingJoinData);
      if (!opened) {
        return;
      }
    }
  };

  const handleModalEndAndJoin = () => {
    setShowActiveMeetingsModal(false);
    if (activeMeetingsData?.active_meetings && pendingJoinData) {
      const meetingIds = activeMeetingsData.active_meetings.map(
        (meeting: any) => meeting.id || meeting.meetingId || meeting.meeting_id,
      );

      endClassroomMeeting({
        meetingIds,
        meetingRole: pendingJoinData.meeting_role,
      });
    }
  };

  return {
    showActiveMeetingsModal,
    activeMeetingsCount: activeMeetingsData?.active_meetings_count || 0,
    activeLoading,
    endLoading,
    handleJoinClick,
    handleModalClose,
    handleModalJoin,
    handleModalEndAndJoin,
  };
}
